import { useState, useEffect } from "react";
import { XMarkIcon, CheckIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";

interface CurriculumItem {
  id: string;
  title: string;
  description: string | null;
  attachment_url?: string | null;
  attachment_filename?: string | null;
  order: number;
  completed: boolean;
  completed_at: string | null;
  progress_id: string | null;
}

interface CurriculumProgressModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
  studentBelt: string | null;
}

export default function CurriculumProgressModal({
  open,
  onClose,
  studentId,
  studentName,
  studentBelt,
}: CurriculumProgressModalProps) {
  const { getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  const [curriculum, setCurriculum] = useState<CurriculumItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    if (open && studentId && studioUser?.studio_id) {
      fetchCurriculumProgress();
    }
  }, [open, studentId, studioUser?.studio_id]);

  const fetchCurriculumProgress = async () => {
    setLoading(true);
    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/student-curriculum-progress/${studioUser.studio_id}/${studentId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCurriculum(data);
      }
    } catch (error) {
      console.error("Failed to fetch curriculum progress:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProgress = async (curriculumId: string, currentStatus: boolean) => {
    setSaving(curriculumId);
    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/student-curriculum-progress/toggle`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            studioId: studioUser.studio_id,
            studentId: studentId,
            curriculumId: curriculumId,
            completed: !currentStatus,
          }),
        }
      );

      if (response.ok) {
        // Update local state
        setCurriculum((prev) =>
          prev.map((item) =>
            item.id === curriculumId
              ? {
                  ...item,
                  completed: !currentStatus,
                  completed_at: !currentStatus ? new Date().toISOString() : null,
                }
              : item
          )
        );
      }
    } catch (error) {
      console.error("Failed to toggle curriculum progress:", error);
    } finally {
      setSaving(null);
    }
  };

  if (!open) return null;

  const completedCount = curriculum.filter((item) => item.completed).length;
  const totalCount = curriculum.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white" id="modal-title">
                  Curriculum Progress: {studentName}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {studentBelt ? `Belt: ${studentBelt}` : "No belt assigned"}
                </p>
              </div>
              <button
                type="button"
                className="bg-white dark:bg-gray-700 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none"
                onClick={onClose}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            {/* Progress Bar */}
            {totalCount > 0 && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span>Progress</span>
                  <span>
                    {completedCount} / {totalCount} ({progressPercentage.toFixed(0)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                  <div
                    className="bg-green-600 dark:bg-green-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Curriculum List */}
            <div className="mt-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading curriculum...</p>
                </div>
              ) : curriculum.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {studentBelt
                      ? `No curriculum items found for ${studentBelt} belt.`
                      : "Student needs a belt assigned to view curriculum."}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {curriculum.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <button
                          type="button"
                          onClick={() => toggleProgress(item.id, item.completed)}
                          disabled={saving === item.id}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                            item.completed
                              ? "bg-green-600 border-green-600 dark:bg-green-500 dark:border-green-500"
                              : "border-gray-300 dark:border-gray-600 hover:border-green-600 dark:hover:border-green-500"
                          } ${saving === item.id ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          {saving === item.id ? (
                            <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                          ) : (
                            item.completed && <CheckIcon className="h-3 w-3 text-white" />
                          )}
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium ${
                            item.completed
                              ? "line-through text-gray-500 dark:text-gray-500"
                              : "text-gray-900 dark:text-white"
                          }`}
                        >
                          {item.title}
                        </p>
                        {item.description && (
                          <p
                            className={`text-xs mt-1 ${
                              item.completed
                                ? "line-through text-gray-400 dark:text-gray-600"
                                : "text-gray-500 dark:text-gray-400"
                            }`}
                          >
                            {item.description}
                          </p>
                        )}
                        {item.attachment_url && (
                          <a
                            href={item.attachment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs mt-1 text-indigo-600 dark:text-indigo-400 hover:underline"
                          >
                            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            {item.attachment_filename || "View document"}
                          </a>
                        )}
                        {item.completed && item.completed_at && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            Completed {new Date(item.completed_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
